import {
  CashOutAmount,
  ESenderRole,
  IContractDetailsResponseData,
  UserProfile,
} from "../api/services/contract/models";
import { IUserInfoResponseData } from "../api/services/user/models";
import {
  resolveCashoutPartyRole,
  userMatchesIdentifier,
} from "./userIdentifier";

/**
 * Roles an automated cashout can settle for. Waffy staff roles (ADMIN*,
 * CLIENT_ADMIN, SERVICE_PROVIDER) are never paid out to, so they are not
 * expected to carry bank or address info.
 */
const CONTRACT_PARTY_ROLES = [
  ESenderRole.PROVIDER,
  ESenderRole.CUSTOMER,
  ESenderRole.BROKER,
];

/** Profile shapes that carry the `hasBankInfo` / `hasAddressInfo` flags. */
type PartyWithDataFlags = Partial<UserProfile> | IUserInfoResponseData;

type PartyMatch = { role: ESenderRole; party: PartyWithDataFlags };

export enum EPartiesDataStatus {
  /** Every party of the contract has both bank info and an address. */
  COMPLETE = "COMPLETE",
  /** At least one party is missing bank info, an address, or both. */
  MISSING = "MISSING",
  /** The payload carries no data flags at all, so nothing can be judged. */
  UNKNOWN = "UNKNOWN",
}

export interface IPartyMissingData {
  role: ESenderRole;
  name?: string;
  phoneNumber?: string;
  missingBankInfo: boolean;
  missingAddressInfo: boolean;
}

export interface IContractPartiesDataStatus {
  status: EPartiesDataStatus;
  /** Only the parties that are missing something — empty unless MISSING. */
  missingParties: IPartyMissingData[];
}

/**
 * Resolves the specific profile behind one `cashOutAmountList` entry, so only
 * parties that are actually due a payout are checked for bank/address data.
 * Returns `null` when the role isn't payable or no matching profile is found.
 */
const getPartyForCashOutAmount = (
  contract: IContractDetailsResponseData,
  cashOutAmount: CashOutAmount
): PartyMatch | null => {
  const role = resolveCashoutPartyRole(
    contract,
    cashOutAmount.userIdentification
  );

  if (!role || !CONTRACT_PARTY_ROLES.includes(role)) {
    return null;
  }

  const matchedProfile = contract.partiesProfiles?.[role]?.find((party) =>
    userMatchesIdentifier(party, cashOutAmount.userIdentification)
  );
  if (matchedProfile) {
    return { role, party: matchedProfile };
  }

  // Quick transactions have no `partiesProfiles`: the same profile object is
  // exposed under `provider` / `customer` on the contract instead.
  const fallbackUser =
    role === ESenderRole.PROVIDER
      ? contract.provider
      : role === ESenderRole.CUSTOMER
      ? contract.customer
      : undefined;

  if (
    fallbackUser &&
    userMatchesIdentifier(fallbackUser, cashOutAmount.userIdentification)
  ) {
    return { role, party: fallbackUser };
  }

  return null;
};

const carriesDataFlags = (party: PartyWithDataFlags): boolean =>
  typeof party.hasBankInfo === "boolean" ||
  typeof party.hasAddressInfo === "boolean";

/**
 * Whether every party of the contract carries the bank info and address an
 * automated cashout needs. A single party missing either one blocks the whole
 * contract, so the result is reported per contract with the offending parties.
 */
export const getContractPartiesDataStatus = (
  contract?: IContractDetailsResponseData | null
): IContractPartiesDataStatus => {
  if (!contract) {
    return { status: EPartiesDataStatus.UNKNOWN, missingParties: [] };
  }

  const relevantCashOutAmounts = (contract.cashOutAmountList || []).filter(
    (cashOutAmount) =>
      (cashOutAmount.cashOutAmount || 0) > 0 &&
      !cashOutAmount.manualWithDrawCashOut
  );

  const parties = relevantCashOutAmounts
    .map((cashOutAmount) => getPartyForCashOutAmount(contract, cashOutAmount))
    .filter((match): match is PartyMatch => !!match);

  // Payloads served before contract-lib 1.4.28 carry no flags at all. Report
  // that as unknown rather than flagging every party of every contract.
  if (!parties.some(({ party }) => carriesDataFlags(party))) {
    return { status: EPartiesDataStatus.UNKNOWN, missingParties: [] };
  }

  const missingParties = parties
    .map(({ role, party }) => ({
      role,
      name: party.profile?.fullName,
      phoneNumber: party.phoneNumber,
      // Parties invited by phone only come back with the flags set to false,
      // so an unregistered party counts as missing data too.
      missingBankInfo: party.hasBankInfo !== true,
      missingAddressInfo: party.hasAddressInfo !== true,
    }))
    .filter(({ missingBankInfo, missingAddressInfo }) =>
      Boolean(missingBankInfo || missingAddressInfo)
    );

  return {
    status: missingParties.length
      ? EPartiesDataStatus.MISSING
      : EPartiesDataStatus.COMPLETE,
    missingParties,
  };
};
